import { Block, CheckCircle, Edit } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { useState } from "react";
import {
  Button,
  Show,
  TabbedShowLayout,
  useDataProvider,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "react-admin";

import { ResourceName } from "../constants/resources";
import { AuthStatus, DataSource, PractitionerStatus, Salutation } from "../generated/graphql";
import { space } from "../theme/designTokens";
import { PractitionerLocationScope } from "../types/practitioner";
import { ActivityList } from "./ActivityList";
import { CustomConfirm } from "./CustomConfirm";
import { SourceSystemChip } from "./fields/SourceSystemChip";
import { LearningProgressSummary } from "./learning/LearningProgressSummary";
import { NotesList } from "./NotesList";
import { PasswordManagementTab } from "./PasswordManagementTab";
import PractitionerEdit from "./PractitionerEdit";
import { PractitionerInfoPanel } from "./PractitionerInfoPanel";
import UserAvatar from "./UserAvatar";

export interface PractitionerRecord {
  id: string;
  userId: string;
  fhirId: string;
  practitionerId: string;
  salutation: Salutation;
  firstName: string;
  lastName: string;
  workerNumber: string;
  name: string;
  email: string;
  telephone?: string;
  avatarUrl?: string;
  organizationName?: string;
  locationNames?: string;
  locationScope: PractitionerLocationScope;
  locations?: Array<{ id: string; name: string; type?: string | null }>;
  status?: PractitionerStatus;
  authStatus?: AuthStatus;
  authStatusValidUntil?: string | null;
  sourceSystem?: DataSource | null;
}

const PractitionerTabs = ({ showLearningProgress }: { showLearningProgress: boolean }) => {
  const translate = useTranslate();
  const record = useRecordContext<PractitionerRecord>();
  const isActive = record?.status !== PractitionerStatus.Inactive;

  return (
    <TabbedShowLayout>
      <TabbedShowLayout.Tab label={translate("tabs.information")}>
        <PractitionerInfoPanel />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label={translate("tabs.passwordManagement")}>
        <PasswordManagementTab />
      </TabbedShowLayout.Tab>

      <TabbedShowLayout.Tab label={translate("tabs.activityHistory")}>
        <ActivityList resource="PractitionerActivities" />
      </TabbedShowLayout.Tab>

      <TabbedShowLayout.Tab label={translate("tabs.notes")}>
        <NotesList />
      </TabbedShowLayout.Tab>
      {showLearningProgress && isActive && (
        <TabbedShowLayout.Tab label={translate("tabs.learningProgress")} path="learning">
          <LearningProgressSummary />
        </TabbedShowLayout.Tab>
      )}
    </TabbedShowLayout>
  );
};

const PractitionerTitle = () => {
  const record = useRecordContext<PractitionerRecord>();
  if (!record) return null;
  return (
    <div>
      {record.organizationName || "Einrichtung"} - Mitarbeiter - {record.name}
    </div>
  );
};

const PractitionerAvatar = () => {
  const record = useRecordContext<PractitionerRecord>();
  if (!record) return null;

  return <UserAvatar record={record} />;
};

const PractitionerShowActions = ({ onEditClick }: { onEditClick: () => void }) => {
  const record = useRecordContext<PractitionerRecord>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    status?: PractitionerStatus;
  }>({ open: false });

  if (!record) return null;

  const handleToggleClick = () => {
    setConfirmState({
      open: true,
      status: record.status === PractitionerStatus.Inactive ? PractitionerStatus.Active : PractitionerStatus.Inactive,
    });
  };

  const handleConfirm = async () => {
    const actionLabel = confirmState.status === PractitionerStatus.Active ? "aktivieren" : "deaktivieren";

    setConfirmState({ open: false });
    try {
      const isImported = Boolean(record.sourceSystem && record.sourceSystem !== DataSource.M4C);
      let warningCode: string | null | undefined;
      if (isImported) {
        const result = await dataProvider.custom("Practitioner", "updateImportedStatus", {
          practitionerId: record.id,
          status: confirmState.status,
        });
        warningCode = result.warningCode;
      } else {
        await dataProvider.update("Practitioner", {
          id: record.id,
          data: { ...record, status: confirmState.status },
          previousData: record,
        });
      }

      if (warningCode === "USER_HAS_NO_EMAIL") {
        notify("create.practitioner.noEmailWarning", { type: "warning" });
        refresh();
        return;
      }
      notify(`Mitarbeiter wurde erfolgreich ${actionLabel}`, {
        type: "success",
      });
      refresh();
    } catch {
      notify(`Fehler beim ${actionLabel} des Mitarbeiters`, { type: "error" });
    }
  };

  const handleCancel = () => {
    setConfirmState({ open: false });
  };

  const isEditable = record.sourceSystem === DataSource.M4C || !record.sourceSystem;
  const sourceLabel =
    record.sourceSystem?.toLowerCase() &&
    translate(`Practitioner.fields.sourceSystem.${record.sourceSystem.toLowerCase()}`);
  const isActive = record.status !== PractitionerStatus.Inactive;
  const actionLabel = isActive ? "deaktivieren" : "aktivieren";

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: `${space.xs}px`, mb: `${space.xs}px` }}>
        <PractitionerAvatar />
        <SourceSystemChip translatePrefix="Practitioner.fields.sourceSystem" />
        <Box
          sx={{
            ml: "auto",
            display: "flex",
            flexWrap: "wrap",
            gap: `${space.xs}px`,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Tooltip
            title={
              isEditable
                ? ""
                : translate("common.editOnlyInSourceSystem", { source: sourceLabel || record.sourceSystem })
            }
          >
            <span>
              <Button
                label="Profil bearbeiten"
                color="secondary"
                onClick={isEditable ? onEditClick : undefined}
                disabled={!isEditable}
              >
                <Edit />
              </Button>
            </span>
          </Tooltip>
          <Button
            label={isActive ? "Profil deaktivieren" : "Profil aktivieren"}
            color={"secondary"}
            onClick={handleToggleClick}
          >
            {isActive ? <Block /> : <CheckCircle />}
          </Button>
        </Box>
      </Box>
      <CustomConfirm
        isOpen={confirmState.open}
        title="Bestätigung"
        content={`Sind Sie sicher, dass Sie diesen Mitarbeiter ${actionLabel} möchten?`}
        onConfirm={handleConfirm}
        onClose={handleCancel}
      />
    </>
  );
};

export const PractitionerShow = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { permissions, isPending: arePermissionsPending } = usePermissions();
  const showLearningProgress = !arePermissionsPending && permissions?.includes(ResourceName.LearningProgress);

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
  };

  return (
    <Show actions={<PractitionerShowActions onEditClick={handleEditClick} />} title={<PractitionerTitle />}>
      <PractitionerTabs showLearningProgress={Boolean(showLearningProgress)} />
      {isEditDialogOpen && <PractitionerEdit open={isEditDialogOpen} onClose={handleEditClose} />}
    </Show>
  );
};

export default PractitionerShow;
